"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Quaire = void 0;
const _get = require("lodash.get");
const constants_1 = require("./constants");
const enums_1 = require("./enums");
const utils_1 = require("./utils");
class Quaire {
    constructor({ items, navigationItems, result }) {
        this._activeItemId = null;
        this._result = {};
        this._validationErrors = {};
        this._selectComponentTypes = [enums_1.QuaireComponentType.SINGLE_SELECT];
        this._rangeComponentTypes = [enums_1.QuaireComponentType.RANGE_SLIDER];
        this._inputComponentTypes = [enums_1.QuaireComponentType.INPUT];
        this._alwaysPossibleFollowUpQuestionComponents = [
            enums_1.QuaireComponentType.RANGE_SLIDER,
            enums_1.QuaireComponentType.INPUT,
        ];
        this._getNextItemIdFromSelectComponents = (activeQuestion, answer) => {
            var _a;
            const option = (_a = activeQuestion === null || activeQuestion === void 0 ? void 0 : activeQuestion.selectOptions) === null || _a === void 0 ? void 0 : _a.find((o) => o.value === answer);
            return option === null || option === void 0 ? void 0 : option.nextItemId;
        };
        // eslint-disable-next-line
        this._getNextItemIdFromRangeComponents = (activeQuestion, answer) => {
            var _a;
            return (_a = activeQuestion === null || activeQuestion === void 0 ? void 0 : activeQuestion.rangeOption) === null || _a === void 0 ? void 0 : _a.nextItemId;
        };
        // eslint-disable-next-line
        this._getNextItemIdFromInputComponents = (activeQuestion, answer) => {
            var _a;
            return (_a = activeQuestion === null || activeQuestion === void 0 ? void 0 : activeQuestion.inputOption) === null || _a === void 0 ? void 0 : _a.nextItemId;
        };
        this.getNavigation = () => {
            const navigationItems = {};
            const activeNavigationItem = this._getActiveQuestionNavigationItem();
            this._navigationItems.forEach((navigationItem) => {
                const hasParent = Boolean(navigationItem.parentId);
                if (!hasParent) {
                    this._addNavigationItem(navigationItems, activeNavigationItem, navigationItem);
                }
                else {
                    this._addChildNavigationItem(navigationItems, activeNavigationItem, navigationItem);
                }
            });
            return Object.values(navigationItems);
        };
        this._items = items;
        this._navigationItems = navigationItems || [];
        if (items.length > 0) {
            this._activeItemId = this._items[0].id;
        }
        if (result) {
            this._result = result;
            this._setActiveItemId();
        }
        this._validate(this.getActiveQuestion());
    }
    _setActiveItemId() {
        let activeQuestion = this.getActiveQuestion();
        if (activeQuestion) {
            let answer = this._result[activeQuestion.resultProperty];
            if (!utils_1.hasAnswer(answer)) {
                return;
            }
            while (activeQuestion) {
                this.saveAnswer(answer);
                const currentQuestion = this.getActiveQuestion();
                if (currentQuestion && activeQuestion.id !== currentQuestion.id) {
                    activeQuestion = currentQuestion;
                    answer = this._result[activeQuestion.resultProperty];
                }
                else {
                    activeQuestion = null;
                    answer = null;
                }
                if (!utils_1.hasAnswer(answer)) {
                    break;
                }
            }
        }
    }
    _getItem(itemId) {
        return this._items.find((i) => i.id === itemId);
    }
    _getQuestionObject(item, dependsOnKeys, selectOptions, rangeOption, inputOption, defaultValue) {
        const value = this._getResultByValueProperty(item.resultProperty);
        let isValid = true;
        if ((!value && item.required) || this._validationErrors[item.id]) {
            isValid = false;
        }
        return {
            id: item.id,
            navigationItemId: item.navigationItemId,
            question: item.question,
            description: item.description,
            required: item.required,
            resultProperty: item.resultProperty,
            value,
            componentType: item.componentType,
            dependsOnQuestions: this._items
                .map((i) => {
                if (dependsOnKeys.includes(i.resultProperty)) {
                    return this._getQuestion(i.id);
                }
                return null;
            })
                .filter((question) => question !== null),
            selectOptions,
            rangeOption,
            inputOption,
            defaultValue,
            isValid,
            nextItemId: item.nextItemId,
            valueHasChanged: false,
        };
    }
    _getDependencyPath(item) {
        const path = [];
        item.dependsOnResultProperties.forEach((resultProperty) => {
            const resultPropertyValue = this._getResultByValueProperty(resultProperty);
            if (resultPropertyValue) {
                path.push(resultProperty);
                path.push(resultPropertyValue);
            }
        });
        return path;
    }
    _getQuestion(itemId) {
        const item = this._getItem(itemId);
        if (!item) {
            return null;
        }
        let selectOptions;
        let rangeOption;
        let inputOption;
        let defaultValue;
        if (item.dependsOnResultProperties.length > 0) {
            const path = this._getDependencyPath(item);
            selectOptions = _get(item.selectOptions, path, null);
            rangeOption = _get(item.rangeOption, path, null);
            inputOption = _get(item.inputOption, path, null);
            defaultValue = _get(item.defaultValue, path, null);
        }
        else {
            selectOptions = item.selectOptions ? item.selectOptions : null;
            rangeOption = item.rangeOption ? item.rangeOption : null;
            inputOption = item.inputOption ? item.inputOption : null;
            defaultValue = item.defaultValue ? item.defaultValue : null;
        }
        return this._getQuestionObject(item, item.dependsOnResultProperties, selectOptions, rangeOption, inputOption, defaultValue);
    }
    _getQuestionByNavigationItemId(categoryId) {
        let item = this._items
            .filter((item) => utils_1.hasAnswer(this._result[item.resultProperty]))
            .find((i) => i.navigationItemId === categoryId);
        if (!item) {
            item = this._items.find((i) => i.navigationItemId === categoryId);
        }
        return this._getQuestion(item === null || item === void 0 ? void 0 : item.id);
    }
    _getResultByValueProperty(valueProperty) {
        return this._result[valueProperty] === undefined ? null : this._result[valueProperty];
    }
    _getActiveQuestionNavigationItem() {
        const navigationItem = this._navigationItems.find((bc) => { var _a; return bc.id === ((_a = this.getActiveQuestion()) === null || _a === void 0 ? void 0 : _a.navigationItemId); });
        return navigationItem || null;
    }
    _validateSelectComponent(question, currentAnswer) {
        const option = question.selectOptions && question.selectOptions.find((o) => o.value === currentAnswer);
        if (question.required && !option) {
            this._result[question.resultProperty] = null;
            this._validationErrors[question.id] = enums_1.QuaireValidationError.REQUIRED;
        }
    }
    _validateRangeComponent(question, activeQuestion) {
        const isActiveQuestionADependency = !!question.dependsOnQuestions.find((dq) => dq.resultProperty === (activeQuestion === null || activeQuestion === void 0 ? void 0 : activeQuestion.resultProperty));
        if (isActiveQuestionADependency && (activeQuestion === null || activeQuestion === void 0 ? void 0 : activeQuestion.required) && activeQuestion.valueHasChanged) {
            delete this._result[question.resultProperty];
            this._validationErrors[question.id] = enums_1.QuaireValidationError.REQUIRED;
        }
    }
    _validateGenericComponent(isQuestionInCurrentFlow, question, activeQuestion, currentAnswer) {
        if (isQuestionInCurrentFlow &&
            !utils_1.hasAnswer(currentAnswer) &&
            question.required &&
            !this._validationErrors[question.id]) {
            this._validationErrors[question.id] = enums_1.QuaireValidationError.REQUIRED;
        }
    }
    _validate(activeQuestion) {
        this._items.forEach((item) => {
            const question = this._getQuestion(item.id);
            if (question) {
                const currentAnswer = this._getResultByValueProperty(question.resultProperty);
                const possibleFollowUpQuestionIds = [];
                question.dependsOnQuestions.forEach((q) => {
                    var _a;
                    const dependsOnQuestionResult = this._result[q.resultProperty];
                    // only take possible follow up id's based on the current result of the dependent question
                    (_a = q.selectOptions) === null || _a === void 0 ? void 0 : _a.forEach((o) => {
                        if (o.value === dependsOnQuestionResult && o.nextItemId) {
                            possibleFollowUpQuestionIds.push(o.nextItemId);
                        }
                    });
                });
                // Some components always allow a dependent question to be in the flow
                if (this._alwaysPossibleFollowUpQuestionComponents.includes(question.componentType)) {
                    possibleFollowUpQuestionIds.push(question.id);
                }
                const isQuestionInCurrentFlow = question.dependsOnQuestions.length > 0 ? possibleFollowUpQuestionIds.includes(question.id) : true;
                if (!isQuestionInCurrentFlow) {
                    delete this._result[question.resultProperty];
                    delete this._validationErrors[question.id];
                }
                else if (currentAnswer && this._selectComponentTypes.includes(question.componentType)) {
                    this._validateSelectComponent(question, currentAnswer);
                }
                else if (currentAnswer && this._rangeComponentTypes.includes(question.componentType)) {
                    this._validateRangeComponent(question, activeQuestion);
                }
                else {
                    this._validateGenericComponent(isQuestionInCurrentFlow, question, activeQuestion, currentAnswer);
                }
            }
        });
    }
    getResult() {
        return this._result;
    }
    getValidationErrors() {
        return this._validationErrors;
    }
    setActiveQuestionByNavigationItemId(navigationItemId) {
        let quaireItem = this._items.find((item) => item.navigationItemId === navigationItemId);
        if (!quaireItem) {
            const firstChildNavigationItem = this._navigationItems.find((navigationItem) => navigationItem.parentId === navigationItemId);
            quaireItem = this._items.find((item) => item.navigationItemId === (firstChildNavigationItem === null || firstChildNavigationItem === void 0 ? void 0 : firstChildNavigationItem.id));
        }
        this._activeItemId = (quaireItem === null || quaireItem === void 0 ? void 0 : quaireItem.id) || null;
    }
    setActiveQuestionByQuestionId(questionId) {
        this._activeItemId = questionId;
    }
    getActiveQuestion() {
        if (this._activeItemId) {
            return this._getQuestion(this._activeItemId);
        }
        return null;
    }
    saveAnswer(answer) {
        const activeQuestion = this.getActiveQuestion();
        if (activeQuestion) {
            let nextItemId;
            activeQuestion.valueHasChanged = this._result[activeQuestion.resultProperty] !== answer;
            this._result[activeQuestion.resultProperty] = answer;
            delete this._validationErrors[activeQuestion.id];
            if (this._selectComponentTypes.includes(activeQuestion.componentType)) {
                nextItemId = this._getNextItemIdFromSelectComponents(activeQuestion, answer);
            }
            else if (this._rangeComponentTypes.includes(activeQuestion.componentType)) {
                nextItemId = this._getNextItemIdFromRangeComponents(activeQuestion, answer);
            }
            else if (this._inputComponentTypes.includes(activeQuestion.componentType)) {
                nextItemId = this._getNextItemIdFromInputComponents(activeQuestion, answer);
            }
            else {
                nextItemId = activeQuestion.nextItemId;
            }
            if (!nextItemId) {
                nextItemId = activeQuestion.id;
            }
            this._activeItemId = nextItemId;
            this._validate(activeQuestion);
        }
    }
    _getNavigationValue(question, answer) {
        var _a, _b;
        let value;
        if (question && this._selectComponentTypes.includes(question.componentType)) {
            value = (_b = (_a = question === null || question === void 0 ? void 0 : question.selectOptions) === null || _a === void 0 ? void 0 : _a.find((selectOption) => selectOption.value === answer)) === null || _b === void 0 ? void 0 : _b.label;
        }
        else {
            value = answer;
        }
        return value;
    }
    _getNavigationItemObject(activeNavigationItem, navigationItem, question, answer, isParent) {
        const active = (activeNavigationItem === null || activeNavigationItem === void 0 ? void 0 : activeNavigationItem.id) === navigationItem.id;
        const isValid = question.isValid;
        const hasValue = Boolean(answer);
        let value = null;
        if (answer === constants_1.NO_VALUE) {
            value = constants_1.NO_VALUE;
        }
        else if (answer) {
            value = this._getNavigationValue(question, answer);
        }
        const item = {
            id: navigationItem.id,
            name: navigationItem.name,
            value,
            icon: navigationItem.icon,
            active,
            isValid,
            hasValue,
            componentType: question.componentType,
        };
        if (isParent) {
            item.subNavigation = [];
        }
        return item;
    }
    _getNavigationItem(activeNavigationItem, navigationItem, isParent = true) {
        const question = this._getQuestionByNavigationItemId(navigationItem.id);
        if (question) {
            const answer = this._getResultByValueProperty(question.resultProperty);
            return this._getNavigationItemObject(activeNavigationItem, navigationItem, question, answer, isParent);
        }
        return null;
    }
    _addNavigationItem(navigationItems, activeNavigationItem, navigationItem) {
        const newNavigationItem = this._getNavigationItem(activeNavigationItem, navigationItem);
        if (newNavigationItem) {
            navigationItems[navigationItem.id] = newNavigationItem;
        }
    }
    _addChildNavigationItem(navigationItems, activeNavigationItem, navigationItem) {
        var _a, _b;
        const subNavigationItem = this._getNavigationItem(activeNavigationItem, navigationItem, false);
        if (subNavigationItem && navigationItem.parentId) {
            (_b = (_a = navigationItems[navigationItem.parentId]) === null || _a === void 0 ? void 0 : _a.subNavigation) === null || _b === void 0 ? void 0 : _b.push(subNavigationItem);
            navigationItems[navigationItem.parentId].active =
                navigationItems[navigationItem.parentId].active || (activeNavigationItem === null || activeNavigationItem === void 0 ? void 0 : activeNavigationItem.parentId) === navigationItem.parentId;
            navigationItems[navigationItem.parentId].hasValue = true;
        }
    }
    isValid() {
        return Object.keys(this._validationErrors).length === 0;
    }
}
exports.Quaire = Quaire;
